/*
 * Julvärmen product pages (ported from Duck & Pippin's dp-product.js, trimmed to the Julvärmen boards).
 * <jv-gallery>         framed main image as a scroll-snap track, low-key chevrons, thumbnails, "1 / 7" counter
 * <jv-deferred-media>  loads a video or 3D model when its poster is pressed
 * <jv-product-form>    variant picker, add-on checkbox, split add-to-cart button with live price, JV.cart.add
 * <jv-sticky-atc>      phone bar that appears once the main button has scrolled out of view
 * Talks to the theme core through window.JV (assets/jv-theme.js). Emits jv:variant:change and jv:product:state.
 */
(() => {
  'use strict';

  // Several sections load this file; run it once.
  if (window.__jvProductLoaded) return;
  window.__jvProductLoaded = true;

  const JV = window.JV || (window.JV = {});
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  const money = (cents) => (typeof JV.formatMoney === 'function' ? JV.formatMoney(cents) : (Number(cents) / 100).toFixed(2));
  const fill = (template, values) =>
    Object.entries(values).reduce((out, [key, value]) => out.split(`[${key}]`).join(String(value)), template || '');
  const escapeHTML = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
  const readJSON = (root, selector) => {
    const el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  };
  /** The t filter HTML-escapes its output: decode before using a string as text. */
  const decode = (value) => {
    const text = String(value == null ? '' : value);
    if (!text.includes('&')) return text;
    const area = document.createElement('textarea');
    area.innerHTML = text;
    return area.value;
  };
  const readStrings = (root) => {
    const data = readJSON(root, '[data-jv-strings]') || {};
    Object.keys(data).forEach((key) => {
      if (typeof data[key] === 'string') data[key] = decode(data[key]);
    });
    return data;
  };
  const emit = (name, detail, target) => {
    if (typeof JV.emit === 'function') JV.emit(name, detail, target);
    else target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  };
  const setBusy = (button, busy) => {
    if (!button) return;
    if (typeof JV.setBusy === 'function') JV.setBusy(button, busy);
    else {
      button.toggleAttribute('aria-busy', busy);
      button.disabled = busy;
    }
  };
  const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');
  /** The product form a sticky bar belongs to: the nearest one before it in the page. */
  const productFormFor = (el) => {
    const forms = [...document.querySelectorAll('jv-product-form')];
    let found = null;
    forms.forEach((form) => {
      if (form.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) found = form;
    });
    return found || forms[0] || null;
  };

  /* ================================================================== gallery */
  class JvGallery extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.track = this.querySelector('[data-jv-gallery-track]');
      if (!this.track) return;
      this.index = Number(this.dataset.start) || 0;
      this.prevButton = this.querySelector('[data-jv-gallery-prev]');
      this.nextButton = this.querySelector('[data-jv-gallery-next]');
      this.current = this.querySelector('[data-jv-gallery-current]');
      this.live = this.querySelector('[data-jv-gallery-live]');

      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = () => this.goTo(this.index, { smooth: false, announce: false });

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      }
      if (this.index > 0) requestAnimationFrame(() => this.goTo(this.index, { smooth: false, announce: false }));
      else this.setActive(0, { announce: false });
    }

    disconnectedCallback() {
      this._connected = false;
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.cancelAnimationFrame(this._frame);
    }

    get slides() {
      return [...this.track.children];
    }

    onScroll() {
      window.cancelAnimationFrame(this._frame);
      this._frame = window.requestAnimationFrame(() => {
        const width = this.track.clientWidth || 1;
        const index = Math.round(Math.abs(this.track.scrollLeft) / width);
        if (index !== this.index) this.setActive(index);
      });
    }

    onClick(event) {
      const thumb = event.target.closest('[data-jv-thumb]');
      if (thumb) {
        event.preventDefault();
        this.goTo(Number(thumb.dataset.jvThumb) || 0);
        return;
      }
      if (event.target.closest('[data-jv-gallery-prev]')) {
        event.preventDefault();
        this.step(-1);
        return;
      }
      if (event.target.closest('[data-jv-gallery-next]')) {
        event.preventDefault();
        this.step(1);
      }
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!event.target.closest('[data-jv-thumb], [data-jv-gallery-prev], [data-jv-gallery-next], [data-jv-gallery-track]')) return;
      event.preventDefault();
      this.step(event.key === 'ArrowLeft' ? -1 : 1);
      const thumb = this.querySelector(`[data-jv-thumb="${this.index}"]`);
      if (thumb && event.target.closest('[data-jv-thumb]')) thumb.focus({ preventScroll: true });
    }

    /** Chevrons wrap around, as on the boards. */
    step(direction) {
      const count = this.slides.length;
      if (count < 2) return;
      this.goTo((this.index + direction + count) % count);
    }

    goTo(index, { smooth = true, announce = true } = {}) {
      const slides = this.slides;
      if (!slides[index]) return;
      const left = index * this.track.clientWidth;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({ left: rtl ? -left : left, behavior: smooth ? scrollBehavior() : 'auto' });
      this.setActive(index, { announce });
    }

    setActive(index, { announce = true } = {}) {
      const slides = this.slides;
      if (!slides.length) return;
      this.index = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.inert = true;
        }
      });
      this.querySelectorAll('[data-jv-thumb]').forEach((thumb) => {
        if (Number(thumb.dataset.jvThumb) === this.index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.current) this.current.textContent = String(this.index + 1);
      if (this.live && announce) this.live.textContent = `${this.index + 1} / ${slides.length}`;
      this.pauseMedia(slides[this.index]);
    }

    pauseMedia(except) {
      this.track.querySelectorAll('video').forEach((video) => {
        if (!except || !except.contains(video)) video.pause();
      });
      this.track.querySelectorAll('iframe').forEach((frame) => {
        if (except && except.contains(frame)) return;
        try {
          frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          frame.contentWindow.postMessage('{"method":"pause"}', '*');
        } catch (error) {
          /* cross-origin frames may refuse */
        }
      });
    }

    /** Called by <jv-product-form> when the variant changes: show its image (by media id or 1-based position). */
    showMedia({ mediaId, position } = {}) {
      let index = -1;
      if (mediaId) index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0 && position) index = Number(position) - 1;
      if (index >= 0 && index < this.slides.length && index !== this.index) this.goTo(index, { announce: false });
    }
  }

  /* ================================================================== deferred media */
  class JvDeferredMedia extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.poster = this.querySelector('[data-jv-load-media]');
      this.onLoad = () => this.load();
      if (this.poster) this.poster.addEventListener('click', this.onLoad);
    }

    disconnectedCallback() {
      this._connected = false;
      if (this.poster) this.poster.removeEventListener('click', this.onLoad);
    }

    load() {
      if (this.loaded) return;
      const template = this.querySelector('template');
      if (!template || !this.poster) return;
      this.loaded = true;
      this.poster.replaceWith(template.content.cloneNode(true));
      const media = this.querySelector('video, iframe, model-viewer');
      if (!media) return;
      if (media.tagName === 'VIDEO') {
        const playing = media.play();
        if (playing && typeof playing.catch === 'function') playing.catch(() => {});
      }
      if (media.tagName === 'MODEL-VIEWER' && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (errors) => {
              if (errors || !window.Shopify.ModelViewerUI) return;
              this.modelUI = new window.Shopify.ModelViewerUI(media);
            },
          },
        ]);
      }
      media.setAttribute('tabindex', '-1');
      media.focus({ preventScroll: true });
    }
  }

  /* ================================================================== product form */
  class JvProductForm extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.product = readJSON(this, '[data-jv-product-json]') || { variants: [] };
      this.strings = readStrings(this);
      this.variants = Array.isArray(this.product.variants) ? this.product.variants : [];
      this.currentVariant = this.variants.find((v) => v.id === this.product.selected) || this.variants[0] || null;
      this.form = document.getElementById(this.dataset.formId);
      this.gallery = this.querySelector('jv-gallery');
      this.atc = this.querySelector('[data-jv-atc]');
      this.busy = false;

      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      if (this.form) {
        this.form.noValidate = true;
        this.form.addEventListener('submit', this.onSubmit);
      }
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Without a picker block, the plain variant dropdown stays visible and drives the variant.
      if (this.variants.length > 1 && !this.querySelector('[data-jv-picker]')) this.classList.add('jv-mp--plain-select');

      // The browser may restore form state (back/forward cache): start from what the page shows.
      this.syncFromDom();
      this.renderTax();
      this.updateTotals({ announce: false });
    }

    disconnectedCallback() {
      this._connected = false;
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.clearTimeout(this._liveTimer);
    }

    /* ---------------------------------------------------------------- reading state */
    optionElements() {
      return [...this.querySelectorAll('[data-jv-option]')].sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position));
    }

    readOptions() {
      return this.optionElements().map((el) => {
        if (el.dataset.fixed != null) return el.dataset.fixed;
        const select = el.querySelector('select');
        if (select) return select.value;
        const checked = el.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, i) => value === options[i])) || null;
    }

    quantity() {
      const input = this.querySelector('[data-jv-quantity]');
      if (input) return Math.min(99, Math.max(1, parseInt(input.value, 10) || 1));
      return 1;
    }

    checkedAddons() {
      return [...this.querySelectorAll('[data-jv-addon]:checked')];
    }

    totals() {
      const variant = this.currentVariant;
      const qty = this.quantity();
      const price = variant ? variant.price : 0;
      const main = price * qty;
      const addons = this.checkedAddons().reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);
      const compare = variant && variant.compare_at_price > variant.price ? variant.compare_at_price * qty + addons : 0;
      return { qty, main, addons, total: main + addons, compare };
    }

    /* ---------------------------------------------------------------- events */
    onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.matches('[data-jv-variant-id]')) {
        if (this.classList.contains('jv-mp--plain-select') && event.isTrusted) {
          const variant = this.variants.find((v) => String(v.id) === target.value) || null;
          this.setVariant(variant, { fromUser: true, syncInputs: false });
        }
        return;
      }
      if (target.closest('[data-jv-option]')) {
        const variant = this.findVariant(this.readOptions());
        this.setVariant(variant, { fromUser: true, imagePosition: target.dataset.image || null });
        return;
      }
      if (target.matches('[data-jv-addon]')) {
        this.updateTotals();
        return;
      }
      if (target.matches('[data-jv-addon-select]')) {
        // A size chosen for an add-on: the row's checkbox carries the chosen variant and price.
        const row = target.closest('.jv-mp__addon');
        const input = row && row.querySelector('[data-jv-addon]');
        const option = target.options[target.selectedIndex];
        if (input && option) {
          input.dataset.variantId = option.value;
          input.dataset.price = option.dataset.price || '0';
          const price = row.querySelector('[data-jv-addon-price]');
          if (price && option.dataset.money) price.textContent = option.dataset.money;
        }
        this.updateTotals();
        return;
      }
      if (target.matches('[data-jv-quantity]')) {
        target.value = String(this.quantity());
        this.updateTotals();
      }
    }

    onInput(event) {
      if (event.target instanceof HTMLElement && event.target.matches('[data-jv-quantity]')) this.updateTotals();
    }

    onSubmit(event) {
      event.preventDefault();
      this.add();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const details = event.target.closest('details');
      if (details) details.open = true;
    }

    /* ---------------------------------------------------------------- variants */
    syncFromDom() {
      if (this.variants.length < 2) return;
      if (this.classList.contains('jv-mp--plain-select')) {
        const select = this.querySelector('select[data-jv-variant-id]');
        const variant = select ? this.variants.find((v) => String(v.id) === select.value) : null;
        if (variant && variant !== this.currentVariant) this.setVariant(variant, { syncInputs: false });
        return;
      }
      const options = this.readOptions();
      if (options.some((value) => value == null)) return;
      const variant = this.findVariant(options);
      if (variant !== this.currentVariant) this.setVariant(variant);
      else this.updateOptionStates();
    }

    setVariant(variant, { fromUser = false, syncInputs = true, imagePosition = null } = {}) {
      this.currentVariant = variant;
      this.updateOptionStates();
      if (variant) {
        if (syncInputs) {
          this.querySelectorAll('[data-jv-variant-id]').forEach((input) => {
            if (input.value !== String(variant.id)) input.value = String(variant.id);
          });
        }
        if (fromUser) {
          this.updateURL(variant);
          if (this.gallery && typeof this.gallery.showMedia === 'function') {
            this.gallery.showMedia({ mediaId: variant.featured_media || null, position: imagePosition });
          }
        }
      }
      this.renderPrice();
      this.renderTax();
      this.renderSpecs();
      this.updateTotals({ announce: fromUser });
      emit('jv:variant:change', { variant, sectionId: this.dataset.sectionId, productId: this.product.id }, this);
    }

    updateURL(variant) {
      if (!window.location.pathname.includes('/products/')) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* history API unavailable */
      }
    }

    valueState(position, value, selected) {
      const candidates = this.variants.filter(
        (variant) => variant.options[position] === value && variant.options.every((option, i) => i === position || option === selected[i])
      );
      if (!candidates.length) return 'missing';
      return candidates.some((variant) => variant.available) ? 'ok' : 'soldout';
    }

    updateOptionStates() {
      const selected = this.readOptions();
      const s = this.strings;
      this.optionElements().forEach((el) => {
        if (el.dataset.fixed != null) return;
        const position = Number(el.dataset.position) - 1;
        const legendValue = el.querySelector('[data-jv-selected-value]');
        if (legendValue) legendValue.textContent = selected[position] || '';
        const select = el.querySelector('select');
        if (select) {
          [...select.options].forEach((option) => {
            if (!option.dataset.label) option.dataset.label = option.value;
            const state = this.valueState(position, option.value, selected);
            option.textContent =
              option.dataset.label + (state === 'soldout' ? ` (${s.valueSoldOut})` : state === 'missing' ? ` (${s.valueUnavailable})` : '');
          });
          return;
        }
        el.querySelectorAll('input[type="radio"]').forEach((input) => {
          const label = el.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (!label) return;
          const state = this.valueState(position, input.value, selected);
          label.classList.toggle('is-unavailable', state !== 'ok');
          const status = label.querySelector('[data-jv-value-status]');
          if (status) status.textContent = state === 'soldout' ? s.valueSoldOut || '' : state === 'missing' ? s.valueUnavailable || '' : '';
        });
      });
    }

    renderPrice() {
      const el = this.querySelector('[data-jv-price]');
      const variant = this.currentVariant;
      if (!el || !variant) return;
      const s = this.strings;
      const onSale = variant.compare_at_price > variant.price;
      let html = `<span class="jv-price${onSale ? ' jv-price--sale' : ''}">`;
      if (onSale) html += `<span class="jv-vh">${escapeHTML(s.salePrice)}</span>`;
      html += `<span class="jv-price__current">${escapeHTML(money(variant.price))}</span>`;
      if (onSale) {
        if (this.product.bundle) {
          html += `<span class="jv-price__separate"><s class="jv-price__compare">${escapeHTML(money(variant.compare_at_price))}</s> ${escapeHTML(s.separately)}</span>`;
        } else {
          html += `<span class="jv-vh">${escapeHTML(s.regularPrice)}</span><s class="jv-price__compare">${escapeHTML(money(variant.compare_at_price))}</s>`;
        }
        if (el.dataset.showSave === 'true') {
          const percent = Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          html += `<span class="jv-price__save">${escapeHTML(fill(s.savePercent, { percent }))}</span>`;
        }
      }
      html += '</span>';
      if (el.innerHTML.trim() !== html) el.innerHTML = html;
    }

    freeShipping(variant) {
      const free = Number(this.product.freeCents) || 0;
      return Boolean(variant && free > 0 && variant.requires_shipping !== false && variant.price >= free);
    }

    renderTax() {
      const el = this.querySelector('[data-jv-tax]');
      if (!el) return;
      const free = el.dataset.free === 'true' && this.freeShipping(this.currentVariant);
      const text = free ? this.strings.taxFree : this.strings.tax;
      if (text && el.textContent !== text) el.textContent = text;
    }

    renderSpecs() {
      const variant = this.currentVariant;
      if (!variant) return;
      this.querySelectorAll('[data-jv-spec-option]').forEach((el) => {
        const value = variant.options[Number(el.dataset.jvSpecOption) - 1];
        if (value != null) el.textContent = value;
      });
    }

    updateTotals({ announce = true } = {}) {
      const totals = this.totals();
      const s = this.strings;
      const variant = this.currentVariant;
      if (this.atc) {
        const label = this.atc.querySelector('[data-jv-atc-label]');
        const price = this.atc.querySelector('[data-jv-atc-price]');
        let text = s.addToCart;
        let disabled = false;
        if (!variant) {
          text = s.unavailable;
          disabled = true;
        } else if (!variant.available) {
          text = s.soldOut;
          disabled = true;
        }
        if (label && text) label.textContent = text;
        if (price) {
          price.textContent = money(totals.total);
          price.hidden = disabled;
        }
        if (!this.busy) this.atc.disabled = disabled;
      }

      const dynamic = this.querySelector('[data-jv-dynamic]');
      if (dynamic) {
        const hasAddons = totals.addons > 0;
        dynamic.hidden = hasAddons || !variant || !variant.available;
        const note = this.querySelector('[data-jv-dynamic-note]');
        if (note) note.hidden = !hasAddons;
      }

      // One live region for the buy box, so a change is announced once.
      const live = this.querySelector('[data-jv-total-live]');
      if (live && announce) {
        let message = s.unavailable;
        if (variant) message = variant.available ? fill(s.totalLive, { total: money(totals.total) }) : s.soldOut;
        window.clearTimeout(this._liveTimer);
        this._liveTimer = window.setTimeout(() => {
          live.textContent = message || '';
        }, 450);
      }
      this.emitState(totals);
    }

    emitState(totals = this.totals()) {
      const variant = this.currentVariant;
      this.state = {
        sectionId: this.dataset.sectionId,
        variant,
        total: totals.total,
        compare: totals.compare,
        quantity: totals.qty,
        available: Boolean(variant && variant.available),
        freeShipping: this.freeShipping(variant),
        image: (variant && variant.image) || this.product.image || null,
        busy: this.busy,
      };
      this.dispatchEvent(new CustomEvent('jv:product:state', { bubbles: true, detail: this.state }));
    }

    /* ---------------------------------------------------------------- add to cart */
    formData() {
      if (!this.form) return null;
      try {
        return new FormData(this.form);
      } catch (error) {
        return null;
      }
    }

    /** Line item properties apps add to the product form (properties[...]), so Ajax posts the same as the form. */
    collectProperties() {
      const properties = {};
      const data = this.formData();
      if (!data) return properties;
      data.forEach((value, name) => {
        const match = /^properties\[(.*)\]$/.exec(name || '');
        if (!match || typeof value !== 'string') return;
        const text = value.trim();
        if (text) properties[match[1]] = text;
      });
      return properties;
    }

    requestAdd() {
      return this.add({ fromSticky: true });
    }

    showFormError(message) {
      const error = this.querySelector('[data-jv-form-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    setBusyState(busy) {
      this.busy = busy;
      setBusy(this.atc, busy);
      this.emitState();
      if (!busy) this.updateTotals({ announce: false });
    }

    async add({ fromSticky = false } = {}) {
      if (this.busy) return false;
      const variant = this.currentVariant;
      if (!variant || !variant.available) {
        if (fromSticky) this.scrollToPicker();
        return false;
      }
      this.showFormError('');

      const main = { id: variant.id, quantity: this.quantity() };
      const properties = this.collectProperties();
      if (Object.keys(properties).length) main.properties = properties;
      const data = this.formData();
      const sellingPlan = data ? data.get('selling_plan') : null;
      if (typeof sellingPlan === 'string' && sellingPlan) main.selling_plan = Number(sellingPlan) || sellingPlan;
      const items = [main];
      this.checkedAddons().forEach((input) => {
        const id = Number(input.dataset.variantId);
        if (id) items.push({ id, quantity: 1 });
      });

      if (!JV.cart || typeof JV.cart.add !== 'function') {
        if (this.form) this.form.submit();
        return false;
      }

      this.setBusyState(true);
      try {
        await JV.cart.add(items, { source: 'product' });
        this.checkedAddons().forEach((input) => {
          input.checked = false;
        });
        // The cart drawer confirms the add (drawer or toast). Without any cart section on the page, say it here.
        const cartType = JV.config && JV.config.cartType;
        if (cartType !== 'page' && !document.querySelector('[data-jv-cart-section]') && typeof JV.toast === 'function') {
          JV.toast(this.strings.added);
        }
        return true;
      } catch (error) {
        const message = (error && error.message) || this.strings.error;
        this.showFormError(message);
        if (typeof JV.toast === 'function') JV.toast(message, { tone: 'error' });
        return false;
      } finally {
        this.setBusyState(false);
      }
    }

    scrollToPicker() {
      const picker = this.querySelector('[data-jv-picker]') || this.querySelector('.jv-mp__info');
      if (picker) picker.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
    }
  }

  /* ================================================================== sticky add to cart */
  class JvStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.bar = this.querySelector('[data-jv-sticky-bar]');
      this.button = this.querySelector('[data-jv-sticky-add]');
      this.onState = this.onState.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onMedia = this.onMedia.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.media = window.matchMedia(this.dataset.showOn === 'all' ? 'all' : '(max-width: 749px)');
      this.setVisible(false);
      if (!this.bar || !this.button) return;
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('jv:product:state', this.onState);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      this.media.addEventListener('change', this.onMedia);
      customElements.whenDefined('jv-product-form').then(() => this.bind());
    }

    disconnectedCallback() {
      this._connected = false;
      document.removeEventListener('jv:product:state', this.onState);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      if (this.media) this.media.removeEventListener('change', this.onMedia);
      if (this.button) this.button.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
      document.documentElement.style.removeProperty('--jv-sticky-atc-h');
      document.documentElement.classList.remove('jv-has-sticky-atc');
    }

    /** Finds the main product form and watches its add-to-cart button. */
    bind() {
      if (!this._connected) return;
      if (this.observer) this.observer.disconnect();
      this.form = productFormFor(this);
      if (!this.form) {
        this.setVisible(false);
        return;
      }
      if (this.form.state) this.render(this.form.state);
      const target = this.form.querySelector('[data-jv-atc]') || this.form;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.setVisible(this.pastButton && this.media.matches);
        },
        { threshold: 0 }
      );
      this.observer.observe(target);
    }

    onSectionLoad(event) {
      if (event.target && event.target.querySelector && event.target.querySelector('jv-product-form')) {
        window.requestAnimationFrame(() => this.bind());
      }
    }

    onMedia() {
      this.setVisible(Boolean(this.pastButton) && this.media.matches);
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      if (this.bar) {
        this.bar.inert = !visible;
        this.bar.setAttribute('aria-hidden', String(!visible));
      }
      const height = visible && this.bar ? this.bar.offsetHeight : 0;
      document.documentElement.classList.toggle('jv-has-sticky-atc', Boolean(height));
      if (height) document.documentElement.style.setProperty('--jv-sticky-atc-h', `${height}px`);
      else document.documentElement.style.removeProperty('--jv-sticky-atc-h');
    }

    onState(event) {
      if (!this.form || event.target !== this.form) return;
      this.render(event.detail);
    }

    render(state) {
      if (!state) return;
      const price = this.querySelector('[data-jv-sticky-price]');
      const free = this.querySelector('[data-jv-sticky-free]');
      const label = this.button ? this.button.querySelector('[data-jv-sticky-label]') : null;
      const image = this.querySelector('[data-jv-sticky-image] img');
      if (price) price.textContent = money(state.total);
      if (free) free.hidden = !state.freeShipping;
      if (image && state.image && image.dataset.src !== state.image) {
        image.dataset.src = state.image;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = state.image;
      }
      if (this.button) {
        this.button.disabled = !state.available || state.busy;
        this.button.toggleAttribute('aria-busy', Boolean(state.busy));
        if (label) label.textContent = state.available ? this.button.dataset.labelAdd : this.button.dataset.labelSoldOut;
      }
    }

    onClick(event) {
      event.preventDefault();
      if (this.form && document.contains(this.form) && typeof this.form.requestAdd === 'function') this.form.requestAdd();
      else this.bind();
    }
  }

  // "Visa fler omdömen": shows the next batch of hidden review cards; the button goes once all are shown.
  const showMoreReviews = (button) => {
    const main = button.closest('.jv-reviews__main');
    if (!main) return;
    const step = Math.max(1, Number(button.dataset.jvReviewsMore) || 4);
    const hidden = [...main.querySelectorAll('[data-jv-review-more][hidden]')];
    const batch = hidden.slice(0, step);
    batch.forEach((card) => card.removeAttribute('hidden'));
    if (hidden.length <= step) button.closest('.jv-reviews__more')?.remove();
    const rail = main.querySelector('jv-rail');
    if (rail && typeof rail.update === 'function') rail.update();
    const first = batch[0] && batch[0].querySelector('.jv-reviews__article');
    if (first) {
      first.setAttribute('tabindex', '-1');
      first.focus({ preventScroll: true });
    }
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-jv-reviews-more]');
    if (button) showMoreReviews(button);
  });
  // Theme editor: a hidden card opens when its block is selected in the sidebar.
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target.matches && event.target.matches('[data-jv-review-more][hidden]')) event.target.removeAttribute('hidden');
  });

  define('jv-gallery', JvGallery);
  define('jv-deferred-media', JvDeferredMedia);
  define('jv-product-form', JvProductForm);
  define('jv-sticky-atc', JvStickyAtc);
})();
